import { Dispatch, PropsWithChildren, SetStateAction, useCallback, useMemo, useState } from 'react';
import { createContext, useContext } from 'react';

export interface StepperContextProps<T extends readonly string[], K> {
  steps: T;
  initialStep?: string;
  step: string;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: T[number]) => void;
  contextData: Partial<K> | null;
  setContextData: Dispatch<SetStateAction<Partial<K>>>;
  contextErrors: ContextErrors;
  setContextError: (key: string, error: string) => void;
  clearContextErrors: () => void;
  currentStepIndex: number;
  hasNextStep: boolean;
  hasPreviousStep: boolean;
  resetStepper: () => void;
}

export type StepperProviderProps<T extends readonly string[]> = {
  steps: T;
  initialStep?: T[number];
};

const StepperContext = createContext<StepperContextProps<readonly string[], any>>(undefined!);

type ContextErrors = {
  [key: string]: string[];
};

export const StepperContextProvider = <T extends readonly string[]>({
  children,
  steps,
  initialStep,
}: PropsWithChildren<StepperProviderProps<T>>) => {
  const [contextData, setContextData] = useState<any>(null);
  const [contextErrors, setContextErrors] = useState<ContextErrors>({});
  const [step, setStep] = useState<T[number]>(initialStep ?? steps[0]);

  const currentStepIndex = useMemo(() => steps.indexOf(step), [step, steps]);
  const hasNextStep = useMemo(() => currentStepIndex < steps.length - 1, [currentStepIndex, steps]);
  const hasPreviousStep = useMemo(() => currentStepIndex > 0, [currentStepIndex]);

  const setContextError = useCallback((key: string, error: string) => {
    setContextErrors((prev) => ({
      ...prev,
      [key]: prev[key] ? [...prev[key], error] : [error],
    }));
  }, []);

  const clearContextErrors = useCallback(() => {
    setContextErrors({});
  }, []);

  const nextStep = useCallback(() => {
    if (hasNextStep) {
      setStep(steps[currentStepIndex + 1]);
    }
  }, [currentStepIndex, hasNextStep, steps]);

  const previousStep = useCallback(() => {
    if (hasPreviousStep) {
      setStep(steps[currentStepIndex - 1]);
    }
  }, [currentStepIndex, hasPreviousStep, steps]);

  const goToStep = useCallback(
    (step: T[number]) => {
      if (steps.includes(step)) {
        setStep(step);
      }
    },
    [setStep, steps]
  );

  const resetStepper = useCallback(() => {
    goToStep(steps[0]);
    setContextData(null);
    clearContextErrors();
  }, [clearContextErrors, goToStep, steps]);

  return (
    <StepperContext.Provider
      value={{
        steps,
        initialStep,
        step,
        nextStep,
        previousStep,
        goToStep,
        contextData,
        setContextData,
        contextErrors,
        setContextError,
        clearContextErrors,
        currentStepIndex,
        hasNextStep,
        hasPreviousStep,
        resetStepper,
      }}
    >
      {children}
    </StepperContext.Provider>
  );
};

export function useStepperContext<T extends readonly string[], K>(): StepperContextProps<T, K> {
  const context = useContext(StepperContext);

  if (typeof context === 'undefined') {
    throw new Error('useStepperContext should be used within the StepperContext provider!');
  }

  return context as unknown as StepperContextProps<T, K>;
}
